import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { api } from "../api";
import { baseURL, appClientDetails } from "../api-types";
import { SsoProviderType } from "./types";

export interface SsoProviderConfig {
  id: string;
  organisationId: string;
  providerType: SsoProviderType;
  allowedDomains: string[];
  allowedTenantIds: string[];
  autoProvisionUsers: boolean;
  defaultRoleId: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSsoProviderRequest {
  providerType: SsoProviderType;
  allowedDomains: string[];
  allowedTenantIds: string[];
  autoProvisionUsers?: boolean;
  defaultRoleId?: number;
}

export interface UpdateSsoProviderRequest {
  allowedDomains?: string[];
  allowedTenantIds?: string[];
  autoProvisionUsers?: boolean;
  defaultRoleId?: number;
  enabled?: boolean;
}

enum SsoQueryKeys {
  SSO_PROVIDERS = "sso-providers",
  SSO_DISCOVERY = "sso-discovery",
}

// ─── Pre-auth (login flow) ─────────────────────────────────

// Session key for the in-app path to return to after an SSO round-trip.
// SsoLoginButton writes it before redirecting to the IdP; SsoCallbackPage reads
// and clears it on success. Kept here so producer and consumer share one literal.
export const SSO_RETURN_PATH_KEY = "ssoReturnPath";

export interface SsoDiscoveryProvider {
  providerType: SsoProviderType;
  organisationId: string;
}

export interface SsoTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  scope: string;
}

/**
 * Discover SSO providers configured for the given email domain.
 * Used by the login form to decide whether to show an SSO sign-in button.
 */
export const useDiscoverSsoProviders = (email: string) => {
  const trimmed = email.trim().toLowerCase();
  const isCandidate = trimmed.includes("@") && trimmed.split("@")[1]?.includes(".");

  const { data, ...rest } = useQuery<SsoDiscoveryProvider[]>(
    [SsoQueryKeys.SSO_DISCOVERY, trimmed],
    async () => {
      const response = await fetch(
        `${baseURL}/oauth/sso/providers?email=${encodeURIComponent(trimmed)}`,
        { credentials: "same-origin", headers: { Accept: "application/json" } }
      );
      if (!response.ok) return [];
      const body = await response.json();
      return body.providers || [];
    },
    {
      enabled: isCandidate,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    }
  );

  return { providers: data || [], ...rest };
};

/**
 * Get the OIDC authorization URL to redirect the user to.
 * Triggered on click; mutation gives us isLoading state for the button.
 */
export const useGetSsoAuthorizationUrlMutation = () => {
  return useMutation(
    async ({
      providerType,
      organisationId,
    }: {
      providerType: SsoProviderType;
      organisationId: string;
    }) => {
      const redirectUri = `${window.location.origin}/auth/sso/callback`;
      const params = new URLSearchParams({
        provider_type: providerType,
        organisation_id: organisationId,
        client_id: appClientDetails.client_id,
        redirect_uri: redirectUri,
      });
      const response = await fetch(`${baseURL}/oauth/sso/authorize?${params}`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        throw new Error("Failed to initiate SSO login");
      }
      const body = await response.json();
      return body.authorization_url as string;
    }
  );
};

/**
 * Exchange the OIDC authorization code (returned by Azure AD on the callback URL)
 * for a Tether access/refresh token pair. Triggered once on the callback page.
 *
 * Public-client flow: only `client_id` is sent. PKCE + the single-use state token
 * (stored server-side) authenticate the request; no secret is needed and none is
 * shipped in the browser bundle.
 */
export const useExchangeSsoCodeMutation = () => {
  return useMutation(
    async ({ state, code }: { state: string; code: string }) => {
      const response = await fetch(`${baseURL}/oauth/sso/callback`, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          state,
          code,
          client_id: appClientDetails.client_id,
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error_description || "SSO authentication failed");
      }
      return response.json() as Promise<SsoTokenResponse>;
    }
  );
};

// ─── Authenticated admin CRUD ──────────────────────────────

export const useGetSsoProviders = () => {
  const { data, ...rest } = useQuery<{ providers: SsoProviderConfig[] }>(
    [SsoQueryKeys.SSO_PROVIDERS],
    () => api.get("v2/sso"),
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );
  return { providers: data?.providers || [], ...rest };
};

export const useCreateSsoProviderMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    (data: CreateSsoProviderRequest) => api.post("v2/sso", data) as Promise<SsoProviderConfig>,
    {
      onError: (error: Error) => {
        toast({
          title: "Unable to create SSO provider",
          description: error?.message,
          status: "error",
          isClosable: true,
          position: "top-right",
        });
      },
      onSuccess: () => {
        toast({
          title: "SSO provider created",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        queryClient.invalidateQueries(SsoQueryKeys.SSO_PROVIDERS);
      },
    }
  );
};

export const useUpdateSsoProviderMutation = (id: string) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    (data: UpdateSsoProviderRequest) => api.put(`v2/sso/${id}`, data) as Promise<SsoProviderConfig>,
    {
      onError: (error: Error) => {
        toast({
          title: "Unable to update SSO provider",
          description: error?.message,
          status: "error",
          isClosable: true,
          position: "top-right",
        });
      },
      onSuccess: () => {
        toast({
          title: "SSO provider updated",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        queryClient.invalidateQueries(SsoQueryKeys.SSO_PROVIDERS);
      },
    }
  );
};

export const useDeleteSsoProviderMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation((id: string) => api.del(`v2/sso/${id}`), {
    onError: (error: Error) => {
      toast({
        title: "Unable to delete SSO provider",
        description: error?.message,
        status: "error",
        isClosable: true,
        position: "top-right",
      });
    },
    onSuccess: () => {
      toast({
        title: "SSO provider deleted",
        status: "success",
        isClosable: true,
        position: "top-right",
      });
      queryClient.invalidateQueries(SsoQueryKeys.SSO_PROVIDERS);
    },
  });
};

